import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { isValidDate } from "lib/date_utils";

import { CancellationFeeTier } from "./graphql.generated";

type ServedCancellationFeeTier = Pick<
  CancellationFeeTier,
  "refundPercentage" | "cancellationAllowedBefore"
>;

/**
 * What the server says about cancelling one booking, named as the quote and the
 * booking carry it so either can be passed as-is:
 * - `cancellationFeeTiers`: every refund step with the instant it closes,
 *   served on a quote for the chosen date or frozen onto the booking. An empty
 *   list means no refund at all.
 * - `cancellationAllowedBefore`: the booking's frozen last moment to cancel,
 *   used only when no tiers are served (a member or multi-pass booking, or one
 *   made before tiers were frozen). A quote doesn't carry it.
 * - `cancellationAllowed`: false when the booking can't be cancelled at all,
 *   read only when neither of the above is served.
 */
export interface ServedCancellation {
  cancellationFeeTiers?: ServedCancellationFeeTier[] | null;
  cancellationAllowedBefore?: string | null;
  cancellationAllowed?: boolean;
}

function formatCutoff(cutoff: Date, locationTimezone: string): string {
  return format(
    utcToZonedTime(cutoff, locationTimezone),
    "hh:mm a 'on' MMM do",
  );
}

// A tier's cutoff is ISO 8601. A booking's comes from dsgraphql as epoch
// milliseconds (a Date sent through a GraphQL String) until dsgraphql serves
// it as ISO too.
function parseCutoff(cutoff?: string | null): Date | null {
  if (!cutoff) {
    return null;
  }

  const instant = /^\d+$/.test(cutoff)
    ? new Date(Number(cutoff))
    : new Date(cutoff);
  return isValidDate(instant) ? instant : null;
}

// Every tier still open, earliest deadline first, each with the instant the
// server says it closes. The server counts working days against the location's
// open days and block-outs, which the browser cannot see, so the deadline is
// stated as served rather than re-derived here.
function getServedTiersCancellationPolicyText(
  tiers: ServedCancellationFeeTier[],
  locationTimezone: string,
  now: Date,
): string {
  const open = tiers
    .flatMap(({ refundPercentage, cancellationAllowedBefore }) => {
      const cutoff = parseCutoff(cancellationAllowedBefore);
      return cutoff ? [{ refundPercentage, cutoff }] : [];
    })
    .filter(
      ({ refundPercentage, cutoff }) => refundPercentage > 0 && cutoff > now,
    )
    .sort((a, b) => a.cutoff.getTime() - b.cutoff.getTime());

  if (open.length === 0) {
    return "Non-refundable";
  }

  return open
    .map(({ refundPercentage, cutoff }) => {
      const when = formatCutoff(cutoff, locationTimezone);

      return refundPercentage === 100
        ? `Free cancellation until ${when}`
        : `${refundPercentage}% refund if cancelled by ${when}`;
    })
    .join(". ");
}

/**
 * The cancellation line for one booking, from what the server served and
 * nothing else. With neither tiers nor a cutoff (no quote yet, or a booking
 * that carries neither) it says nothing rather than guess a deadline, unless
 * the booking is served as not cancellable at all.
 */
export function getCancellationPolicyText(
  served: ServedCancellation | null | undefined,
  locationTimezone: string,
  now: Date = new Date(),
): string {
  const {
    cancellationFeeTiers,
    cancellationAllowedBefore,
    cancellationAllowed,
  } = served ?? {};
  if (cancellationFeeTiers) {
    return getServedTiersCancellationPolicyText(
      cancellationFeeTiers,
      locationTimezone,
      now,
    );
  }

  const cutoff = parseCutoff(cancellationAllowedBefore);
  if (!cutoff) {
    return cancellationAllowed === false ? "Non-refundable" : "";
  }

  return cutoff > now
    ? `Free cancellation until ${formatCutoff(cutoff, locationTimezone)}`
    : "Non-refundable";
}
